import type { TOnboardingAnswerRequirements, TOnboardingQuestionDataType } from "@/api/onboarding";
import { AlertCircle, ArrowRight, ChevronRight, Loader2, Paperclip, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Textarea } from "@/components/ui/Textarea";
import {
  SKIP_UTTERANCE,
  clearDraft,
  loadDraft,
  saveDraft,
} from "@/lib/onboarding/sessionAdapter";
import { getFileExtension, resolveOnboardingAccept } from "@/lib/uploadPolicy";
import { TMessageFile } from "@/types/chat";

type TFileUploadState = {
  file: File;
  progress: number; // 0-100
  status: "pending" | "uploading" | "success" | "error";
  uploadedFile?: TMessageFile;
  error?: string;
};

type TOnboardingMessageInputProps = {
  onSend: (message: string, files?: TMessageFile[]) => void;
  disabled?: boolean;
  /** A turn is in-flight — sending holds the text and offers it for the next question. */
  awaiting?: boolean;
  uploadFile: (file: File, context: { companyId: string; questionId: string }) => Promise<string | null>;
  context: { companyId: string; questionId: string };
  placeholder?: string;
  acceptedFileTypes?: string[]; // e.g., ['pdf', 'pptx', 'docx']
  answerRequirements?: TOnboardingAnswerRequirements | null;
  dataType?: TOnboardingQuestionDataType;
  required?: boolean;
  sendLabel?: string;
  /** A previous answer for a near-duplicate question — offered as a one-tap prefill. */
  prefill?: string | null;
  /** Text handed back into the composer (e.g. "Edit instead" on a failed send). Nonce forces re-apply. */
  restoreText?: { text: string; nonce: number } | null;
};

/**
 * Convert file type array from question metadata to HTML accept attribute format
 * e.g., ['pdf', 'pptx'] -> '.pdf,.pptx'
 * Returns empty string if no file types specified (allows any file type)
 * File types come from question's answer_requirements or metadata
 */
const formatFileTypesForAccept = (fileTypes?: string[]): string => {
  if (!fileTypes || fileTypes.length === 0) {
    // Return empty string to allow all file types when question doesn't specify restrictions
    return "";
  }

  // Add dot prefix to each file type if not already present
  return fileTypes.map((type) => (type.startsWith(".") ? type : `.${type}`)).join(",");
};

/**
 * Create a unique key for a file based on its properties
 * This allows us to track if the same file was previously uploaded
 */
const getFileKey = (file: File): string => {
  return `${file.name}-${file.size}-${file.lastModified}`;
};

/**
 * Validate the answer based on question requirements
 */
type TValidationResult = {
  isValid: boolean;
  errors: string[];
};

const validateAnswer = (
  message: string,
  files: TFileUploadState[],
  requirements?: TOnboardingAnswerRequirements | null,
  dataType?: TOnboardingQuestionDataType,
  acceptedFileTypes?: string[]
): TValidationResult => {
  const errors: string[] = [];

  if (!requirements) {
    return { isValid: true, errors: [] };
  }

  const trimmedMessage = message.trim();
  const successfulFiles = files.filter((f) => f.status === "success");

  // Check required fields
  if (requirements.require_text && !trimmedMessage) {
    errors.push("Text answer is required");
  }

  if (requirements.require_file && successfulFiles.length === 0) {
    errors.push("File upload is required");
  }

  if (requirements.require_either && !trimmedMessage && successfulFiles.length === 0) {
    errors.push("Either text or file is required");
  }

  // Validate text length
  if (trimmedMessage) {
    if (requirements.text_min_length !== null && requirements.text_min_length !== undefined) {
      if (trimmedMessage.length < requirements.text_min_length) {
        errors.push(
          `Text must be at least ${requirements.text_min_length} characters (current: ${trimmedMessage.length})`
        );
      }
    }

    if (requirements.text_max_length !== null && requirements.text_max_length !== undefined) {
      if (trimmedMessage.length > requirements.text_max_length) {
        errors.push(
          `Text must be at most ${requirements.text_max_length} characters (current: ${trimmedMessage.length})`
        );
      }
    }
  }

  // Validate number format and range
  if (requirements.must_be_number || dataType === "number") {
    if (trimmedMessage) {
      const numValue = Number(trimmedMessage.replace(/,/g, ""));
      if (isNaN(numValue)) {
        errors.push("Please enter a valid number");
      } else {
        if (requirements.min_value !== null && requirements.min_value !== undefined) {
          if (numValue < requirements.min_value) {
            errors.push(`Number must be at least ${requirements.min_value}`);
          }
        }
        if (requirements.max_value !== null && requirements.max_value !== undefined) {
          if (numValue > requirements.max_value) {
            errors.push(`Number must be at most ${requirements.max_value}`);
          }
        }
      }
    }
  }

  // Validate file types
  if (acceptedFileTypes && acceptedFileTypes.length > 0) {
    const normalizedAcceptedTypes = acceptedFileTypes.map((type) => type.toLowerCase().replace(/^\./, ""));

    successfulFiles.forEach((fileUpload) => {
      const fileExtension = getFileExtension(fileUpload.file.name);
      if (!normalizedAcceptedTypes.includes(fileExtension)) {
        errors.push(
          `File "${fileUpload.file.name}" is not an accepted type. Accepted types: ${acceptedFileTypes.join(", ")}`
        );
      }
    });
  }

  // Validate file sizes
  if (requirements.max_file_size_mb !== null && requirements.max_file_size_mb !== undefined) {
    const maxSizeBytes = requirements.max_file_size_mb * 1024 * 1024;

    successfulFiles.forEach((fileUpload) => {
      if (fileUpload.file.size > maxSizeBytes) {
        const fileSizeMB = (fileUpload.file.size / (1024 * 1024)).toFixed(2);
        errors.push(
          `File "${fileUpload.file.name}" exceeds maximum size of ${requirements.max_file_size_mb}MB (current: ${fileSizeMB}MB)`
        );
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default function OnboardingMessageInput({
  onSend,
  disabled,
  awaiting = false,
  uploadFile,
  context,
  placeholder = "Type your answer...",
  acceptedFileTypes,
  answerRequirements,
  dataType,
  required,
  sendLabel,
  prefill,
  restoreText,
}: TOnboardingMessageInputProps) {
  const [message, setMessage] = useState("");
  const [fileUploads, setFileUploads] = useState<TFileUploadState[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasAttemptedSend, setHasAttemptedSend] = useState(false);
  // Text sent while a turn was in-flight — offered back as a chip for the next question.
  const [heldText, setHeldText] = useState<string | null>(null);
  // A send pressed while uploads are still running — dispatches once they resolve.
  const [queuedSend, setQueuedSend] = useState<{ text: string } | null>(null);
  const [prefillDismissed, setPrefillDismissed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Track uploaded files by their unique key to reuse IDs
  const uploadedFilesCache = useRef<Map<string, string>>(new Map());
  // Question-declared types, filtered to what the pipeline can actually parse.
  // Falls back to the full KB set when the question declares none.
  const effectiveFileTypes = useMemo(() => resolveOnboardingAccept(acceptedFileTypes), [acceptedFileTypes]);

  // Drafts: restore the previous draft for this question (survives refresh), then
  // persist on every keystroke. Cleared only when the turn is actually sent.
  useEffect(() => {
    setMessage(loadDraft(context.companyId, context.questionId) ?? "");
    setQueuedSend(null);
    setPrefillDismissed(false);
    setHasAttemptedSend(false);
    setValidationErrors([]);
  }, [context.companyId, context.questionId]);

  useEffect(() => {
    saveDraft(context.companyId, context.questionId, message);
  }, [message, context.companyId, context.questionId]);

  // Restore text handed back by the parent (failed-send "Edit instead").
  useEffect(() => {
    if (restoreText) {
      setMessage(restoreText.text);
    }
  }, [restoreText]);

  const hasUploadingFiles = fileUploads.some(
    (upload) => upload.status === "uploading" || upload.status === "pending"
  );
  const hasErroredUploads = fileUploads.some((upload) => upload.status === "error");
  const hasSuccessfulUploads = fileUploads.some((upload) => upload.status === "success");

  // Dispatch a queued send once every upload has resolved. Errored uploads hold
  // the item and surface a "send without it" escape instead of dropping silently.
  useEffect(() => {
    if (!queuedSend || hasUploadingFiles || hasErroredUploads) return;
    const uploadedFiles = fileUploads
      .filter((upload) => upload.status === "success" && upload.uploadedFile?.id)
      .map((upload) => upload.uploadedFile!)
      .filter((file): file is TMessageFile => file.id != null && file.id !== "");
    onSend(queuedSend.text, uploadedFiles.length > 0 ? uploadedFiles : undefined);
    setQueuedSend(null);
    setFileUploads([]);
    setValidationErrors([]);
    setHasAttemptedSend(false);
  }, [queuedSend, hasUploadingFiles, hasErroredUploads, fileUploads, onSend]);

  /** Send just the queued text, abandoning the failed attachment(s). */
  const sendQueuedWithoutFiles = () => {
    if (!queuedSend) return;
    onSend(queuedSend.text);
    setQueuedSend(null);
    setFileUploads([]);
    setValidationErrors([]);
    setHasAttemptedSend(false);
  };

  // Upload files when they're selected
  useEffect(() => {
    if (!uploadFile || !context) {
      return;
    }

    const pendingUploads = fileUploads.filter((upload) => upload.status === "pending");

    if (pendingUploads.length === 0) {
      return;
    }

    const uploadFiles = async () => {
      for (const upload of pendingUploads) {
        setFileUploads((prev) =>
          prev.map((u) => (u.file === upload.file ? { ...u, status: "uploading", progress: 0 } : u))
        );

        try {
          const fileId = await uploadFile(upload.file, context);

          if (!fileId) {
            setFileUploads((prev) =>
              prev.map((u) =>
                u.file === upload.file
                  ? {
                      ...u,
                      status: "error",
                      progress: 0,
                      error: "Upload failed",
                    }
                  : u
              )
            );
            continue;
          }

          // Store the file ID in cache for future reuse
          const fileKey = getFileKey(upload.file);
          uploadedFilesCache.current.set(fileKey, fileId);

          setFileUploads((prev) =>
            prev.map((u) =>
              u.file === upload.file
                ? {
                    ...u,
                    status: "success",
                    progress: 100,
                    uploadedFile: {
                      id: fileId,
                      name: upload.file.name,
                    },
                  }
                : u
            )
          );
        } catch {
          setFileUploads((prev) =>
            prev.map((u) =>
              u.file === upload.file
                ? {
                    ...u,
                    status: "error",
                    progress: 0,
                    error: "Upload failed",
                  }
                : u
            )
          );
        }
      }
    };

    void uploadFiles();
  }, [fileUploads, uploadFile, context]);

  // Run validation whenever message or files change (only if user has attempted to send)
  // This ensures errors only show after submit attempt, not while typing
  useEffect(() => {
    // Only validate if user has attempted to send
    if (hasAttemptedSend) {
      const validation = validateAnswer(message, fileUploads, answerRequirements, dataType, effectiveFileTypes);
      setValidationErrors(validation.errors);
    } else {
      setValidationErrors([]);
    }
  }, [message, fileUploads, answerRequirements, dataType, effectiveFileTypes, hasAttemptedSend]);

  const isOptionalQuestion =
    required === false ||
    (answerRequirements != null &&
      !answerRequirements.require_text &&
      !answerRequirements.require_file &&
      !answerRequirements.require_either);

  const resetComposer = () => {
    setMessage("");
    setFileUploads([]);
    setValidationErrors([]);
    setHasAttemptedSend(false);
    clearDraft(context.companyId, context.questionId);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = () => {
    if (disabled) {
      return;
    }

    // A turn is in-flight — hold the text and offer it back when the next
    // question arrives instead of firing a second frame the backend would read
    // as the answer to that question.
    if (awaiting) {
      if (message.trim()) {
        setHeldText(message.trim());
      }
      setMessage("");
      setValidationErrors([]);
      setHasAttemptedSend(false);
      return;
    }

    // Send pressed while uploads are still running — queue it. The dispatch
    // effect fires once they resolve; a failed upload gets a "send without it"
    // escape rather than silently dropping the answer.
    if (hasContent && hasUploadingFiles) {
      setQueuedSend({ text: message.trim() });
      setMessage("");
      setValidationErrors([]);
      setHasAttemptedSend(false);
      clearDraft(context.companyId, context.questionId);
      return;
    }

    if (!hasContent && !isOptionalQuestion) {
      return;
    }

    // Skipping an optional question: bypass answer validation (it demands
    // content) and tell the assistant explicitly, so the turn reads naturally
    // in the transcript and the AI moves on.
    if (isOptionalQuestion && !hasContent) {
      onSend(SKIP_UTTERANCE);
      resetComposer();
      return;
    }

    // Validate before sending
    setHasAttemptedSend(true);
    const validation = validateAnswer(message, fileUploads, answerRequirements, dataType, effectiveFileTypes);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    if (message.trim() || hasSuccessfulUploads || isOptionalQuestion) {
      const uploadedFiles = fileUploads
        .filter((upload) => upload.status === "success" && upload.uploadedFile?.id)
        .map((upload) => upload.uploadedFile!)
        .filter((file): file is TMessageFile => file.id != null && file.id !== "");

      onSend(message.trim(), uploadedFiles.length > 0 ? uploadedFiles : undefined);
      resetComposer();
    }
  };

  /** Explicit skip on any question — same utterance the optional path sends. */
  const handleSkip = () => {
    if (disabled || awaiting) {
      return;
    }
    onSend(SKIP_UTTERANCE);
    resetComposer();
  };

  const hasContent = message.trim().length > 0 || fileUploads.length > 0;
  const hasValidationErrors = validationErrors.length > 0;
  const canSend = disabled
    ? false
    : awaiting
      ? message.trim().length > 0
      : hasValidationErrors
        ? false
        : hasContent || isOptionalQuestion;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter for new line, Enter to send
    if (e.key === "Enter" && !e.shiftKey && !disabled) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const processFiles = (files: File[]) => {
    const newUploads: TFileUploadState[] = files.map((file) => {
      const fileKey = getFileKey(file);
      const cachedFileId = uploadedFilesCache.current.get(fileKey);

      // If this file was previously uploaded, reuse its ID
      if (cachedFileId) {
        return {
          file,
          progress: 100,
          status: "success" as const,
          uploadedFile: {
            id: cachedFileId,
            name: file.name,
          },
        };
      }

      const fileExtension = getFileExtension(file.name);
      const normalizedAcceptedTypes = effectiveFileTypes.map((type) => type.toLowerCase().replace(/^\./, ""));

      // Validate file type immediately
      if (effectiveFileTypes.length > 0 && !normalizedAcceptedTypes.includes(fileExtension)) {
        return {
          file,
          progress: 0,
          status: "error" as const,
          error: `File type "${fileExtension}" is not accepted. Accepted types: ${effectiveFileTypes.join(", ")}`,
        };
      }

      // Validate file size immediately
      if (answerRequirements?.max_file_size_mb !== null && answerRequirements?.max_file_size_mb !== undefined) {
        const maxSizeBytes = answerRequirements.max_file_size_mb * 1024 * 1024;
        if (file.size > maxSizeBytes) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          return {
            file,
            progress: 0,
            status: "error" as const,
            error: `File size (${fileSizeMB}MB) exceeds maximum of ${answerRequirements.max_file_size_mb}MB`,
          };
        }
      }

      return {
        file,
        progress: 0,
        status: "pending" as const,
      };
    });
    setFileUploads((prev) => [...prev, ...newUploads]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    processFiles(files);
    // Reset the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setFileUploads((prev) => prev.filter((upload) => upload.file !== fileToRemove));
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  return (
    <div
      ref={dropZoneRef}
      className={`relative transition-colors ${isDragging ? "bg-primary/5" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag and drop overlay */}
      {isDragging && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-lg border-2 border-dashed border-primary/50 bg-primary/10 bg-opacity-95">
          <div className="px-4 text-center">
            <Paperclip className="mx-auto mb-1.5 h-8 w-8 text-primary" />
            <p className="text-sm font-medium text-primary">Drop files here to upload</p>
          </div>
        </div>
      )}

      {/* Validation errors */}
      {hasAttemptedSend && validationErrors.length > 0 && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="mb-1 text-sm font-medium text-red-800">Please fix the following errors:</p>
              <ul className="list-inside list-disc space-y-1 text-sm text-red-700">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* File upload progress indicators */}
      {fileUploads.length > 0 && (
        <div className="space-y-2 border-b px-4 pb-2 pt-3">
          {fileUploads.map((upload, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center justify-between">
                  <span className="truncate text-foreground">{upload.file.name}</span>
                  <button
                    onClick={() => handleRemoveFile(upload.file)}
                    className="ml-2 p-1 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Remove file"
                    disabled={upload.status === "uploading"}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {upload.status === "uploading" && (
                  <div className="h-1.5 w-full rounded-full bg-muted">
                    <div
                      className="h-1.5 rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
                {upload.status === "success" && <div className="text-xs text-green-600">Uploaded</div>}
                {upload.status === "error" && (
                  <div className="text-xs text-red-600">{upload.error ?? "Upload failed"}</div>
                )}
                {upload.status === "pending" && <div className="text-xs text-muted-foreground">Waiting to upload...</div>}
              </div>
              {upload.status === "uploading" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </div>
          ))}
        </div>
      )}

      {/* Held text — written while a turn was in-flight, offered for this question */}
      {heldText && !awaiting && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-4 py-2">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            You wrote this while waiting: “{heldText}”
          </span>
          <button
            type="button"
            onClick={() => {
              setMessage(heldText);
              setHeldText(null);
            }}
            className="text-xs font-medium text-primary underline-offset-2 hover:underline"
          >
            Use it
          </button>
          <button
            type="button"
            onClick={() => setHeldText(null)}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            Discard
          </button>
        </div>
      )}

      {/* Repeat-question prefill — a prior answer for a near-duplicate question */}
      {prefill && !prefillDismissed && !message.trim() && !heldText && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-4 py-2">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            You answered this earlier — use it again?
          </span>
          <button
            type="button"
            onClick={() => setMessage(prefill)}
            className="text-xs font-medium text-primary underline-offset-2 hover:underline"
          >
            Use previous answer
          </button>
          <button
            type="button"
            onClick={() => setPrefillDismissed(true)}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Queued send — pressed while an upload was still running */}
      {queuedSend && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-4 py-2">
          {hasErroredUploads ? (
            <>
              <span className="text-xs text-muted-foreground">
                An upload couldn't be attached. Your answer is ready to send.
              </span>
              <button
                type="button"
                onClick={sendQueuedWithoutFiles}
                className="text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                Send without it
              </button>
              <button
                type="button"
                onClick={() => {
                  setMessage(queuedSend.text);
                  setQueuedSend(null);
                }}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                Edit instead
              </button>
            </>
          ) : (
            <>
              <Loader2 className="h-3 w-3 animate-spin text-primary" />
              <span className="text-xs text-muted-foreground">
                Attaching your file — the answer sends as soon as it lands.
              </span>
              <button
                type="button"
                onClick={() => {
                  setMessage(queuedSend.text);
                  setQueuedSend(null);
                }}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                Edit instead
              </button>
            </>
          )}
        </div>
      )}

      <div className="px-4 py-3">
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept={formatFileTypesForAccept(effectiveFileTypes)}
          className="hidden"
        />

        {/* Main input card */}
        <div className="flex items-end gap-2 rounded-2xl border border-border bg-card px-3 py-2 shadow-sm">
          {/* Paperclip icon */}
          <button
            onClick={handleFileInputClick}
            className="mb-1 flex-shrink-0 p-1 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
            aria-label="Attach file"
            disabled={Boolean(disabled)}
            title="Attach file"
          >
            <Paperclip className="h-5 w-5" />
          </button>

          {/* Message textarea */}
          <div className="flex-1">
            <Textarea
              ref={textareaRef}
              placeholder={placeholder ?? "Type your response, or 'done' to generate your summary…"}
              className="border-0 shadow-none focus-visible:ring-0"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={Boolean(disabled)}
              minHeight={40}
              maxHeight={200}
            />
          </div>

          {/* Send / Skip buttons */}
          {(() => {
            const emptyState = !message.trim() && !hasSuccessfulUploads;
            const isSkip = isOptionalQuestion && emptyState;
            return (
              <>
                {/* Universal skip — a ghost affordance on required questions too,
                    with an honest consequence hint. */}
                {!isSkip && emptyState && !awaiting && (
                  <button
                    onClick={handleSkip}
                    disabled={Boolean(disabled)}
                    title="Skip this question — you can fill it in later; skipping may leave gaps in your profile."
                    className="mb-1 flex flex-shrink-0 items-center gap-1 rounded-xl px-2 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                  >
                    Skip
                  </button>
                )}
                {isSkip ? (
                  <button
                    onClick={handleSendMessage}
                    disabled={!canSend}
                    className="mb-1 flex flex-shrink-0 items-center gap-1 rounded-xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                  >
                    Skip
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSendMessage}
                    disabled={!canSend}
                    className="mb-1 flex flex-shrink-0 items-center justify-center rounded-xl bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label={sendLabel ?? "Send"}
                    title={awaiting ? "We'll keep this and offer it for the next question" : undefined}
                  >
                    {queuedSend ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <ArrowRight className="h-5 w-5" />
                    )}
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
